import { RailsAPIBase, type paths } from "../index.client";
import type { Simplify } from "type-fest";

/**
 * Type definitions for deploy operations
 */
export type CreateDeployRequest = NonNullable<
  paths["/api/v1/deploys"]["post"]["requestBody"]
>["content"]["application/json"];

export type CreateDeployResponse = NonNullable<
  paths["/api/v1/deploys"]["post"]["responses"][201]["content"]
>["application/json"];

export type GetDeployResponse = NonNullable<
  paths["/api/v1/deploys/{id}"]["get"]["responses"][200]["content"]
>["application/json"];

// PATCH /api/v1/deploys/{id} is not yet in the swagger spec — type manually
export type UpdateDeployResponse = DeployRecord;

export type TouchDeployResponse = NonNullable<
  paths["/api/v1/deploys/{id}/touch"]["post"]["responses"][200]["content"]
>["application/json"];

export type RollbackDeployResponse = NonNullable<
  paths["/api/v1/deploys/{id}/rollback"]["post"]["responses"][200]["content"]
>["application/json"];

/** Full response type from GET /api/v1/deploys */
export type DeploysListResponse = NonNullable<
  paths["/api/v1/deploys"]["get"]["responses"][200]["content"]
>["application/json"];

/** Request parameters for GET /api/v1/deploys */
export type GetDeploysRequest = paths["/api/v1/deploys"]["get"]["parameters"]["query"];

/** DeployRecord derived from RSwag-generated OpenAPI types (GET /api/v1/deploys/{id}) */
export type DeployRecord = NonNullable<
  paths["/api/v1/deploys/{id}"]["get"]["responses"][200]["content"]
>["application/json"];

export interface CreateDeployParams {
  projectId: number;
  deployType?: "website" | "campaign";
  /**
   * Required when `deployType === "campaign"`. The backend uses it to
   * attach a CampaignDeploy linking back to the campaign — without it,
   * the orchestrator raises "No campaign linked to deploy" on the first
   * CampaignSyncTask. Projects can have multiple campaigns, so the
   * caller must specify which one is being deployed.
   *
   * Use the integer id (matches codebase API standard for body params:
   * project_id, website_id, page_id). UUIDs belong in user-visible URL
   * paths; request bodies use ids.
   */
  campaignId?: number;
}

/**
 * Service for interacting with the Rails Deploy API.
 * Can be used from both frontend and backend (langgraph).
 */
export class DeployAPIService extends RailsAPIBase {
  constructor(options: Simplify<ConstructorParameters<typeof RailsAPIBase>[0]>) {
    super(options);
  }

  /**
   * Lists paginated deploys for a project, with optional status filtering.
   */
  async list(params: GetDeploysRequest): Promise<DeploysListResponse> {
    const client = await this.getClient();
    const response = await client.GET("/api/v1/deploys", {
      params: { query: params },
    });

    if (response.error) {
      throw new Error(`Failed to list deploys: ${JSON.stringify(response.error)}`);
    }

    if (!response.data) {
      throw new Error("Failed to list deploys: No data returned");
    }

    return response.data;
  }

  /**
   * Creates a new deploy with a thread_id stored directly on the record.
   * Called by the deploy graph's first node (initDeploy).
   */
  async create({ projectId, deployType, campaignId }: CreateDeployParams): Promise<DeployRecord> {
    const resolvedDeployType = deployType ?? "website";

    if (resolvedDeployType === "campaign" && !campaignId) {
      throw new Error(
        "campaignId is required when creating a campaign deploy. " +
          "Pass it from page props (Inertia::CampaignDeployProps exposes campaign.id)."
      );
    }

    const body: {
      project_id: number;
      deploy_type: "website" | "campaign";
      campaign_id?: number;
    } = {
      project_id: projectId,
      deploy_type: resolvedDeployType,
    };
    if (campaignId) body.campaign_id = campaignId;

    const client = await this.getClient();
    const response = await client.POST("/api/v1/deploys", {
      body,
    });

    if (response.error) {
      throw new Error(`Failed to create deploy: ${JSON.stringify(response.error)}`);
    }

    if (!response.data) {
      throw new Error("Failed to create deploy: No data returned");
    }

    return response.data satisfies DeployRecord;
  }

  /**
   * Retrieves a deploy by ID
   */
  async get(deployId: number): Promise<DeployRecord> {
    const client = await this.getClient();
    const response = await client.GET("/api/v1/deploys/{id}", {
      params: {
        path: { id: deployId },
      },
    });

    if (response.error) {
      throw new Error(`Failed to get deploy: ${JSON.stringify(response.error)}`);
    }

    if (!response.data) {
      throw new Error("Failed to get deploy: No data returned");
    }

    return response.data satisfies DeployRecord;
  }

  /**
   * Updates a deploy's status, step, etc.
   */
  async update(
    deployId: number,
    updates: { status?: string; current_step?: string; is_live?: boolean; needs_support?: boolean; stacktrace?: string }
  ): Promise<DeployRecord> {
    const client = await this.getClient();
    // PATCH /api/v1/deploys/{id} exists in Rails but is not yet in the swagger spec
    const response = await (client as any).PATCH("/api/v1/deploys/{id}", {
      params: {
        path: { id: deployId },
      },
      body: updates,
    });

    if (!response.data) {
      throw new Error("Failed to update deploy: No data returned");
    }

    return response.data as DeployRecord;
  }

  /**
   * Checks whether the project's website has changed since the last deploy.
   */
  async checkProjectChanges(projectUuid: string): Promise<{ website: boolean }> {
    const client = await this.getClient();
    const response = await (client as any).GET(
      `/api/v1/projects/${projectUuid}/check_changes`,
      {},
    );

    if (response.error) {
      throw new Error(`Failed to check changes: ${JSON.stringify(response.error)}`);
    }
    if (!response.data) {
      throw new Error("Failed to check changes: No data returned");
    }
    return response.data as { website: boolean };
  }

  /**
   * Checks whether the campaign or its website has changed since the
   * last deploy. The campaign argument is required — there's no fallback
   * to "the project's first campaign."
   */
  async checkCampaignChanges(
    campaignUuid: string,
  ): Promise<{ website: boolean; campaign: boolean }> {
    const client = await this.getClient();
    const response = await (client as any).GET(
      `/api/v1/campaigns/${campaignUuid}/check_changes`,
      {},
    );

    if (response.error) {
      throw new Error(`Failed to check changes: ${JSON.stringify(response.error)}`);
    }
    if (!response.data) {
      throw new Error("Failed to check changes: No data returned");
    }
    return response.data as { website: boolean; campaign: boolean };
  }

  /**
   * Rolls back a completed deploy to a previous version.
   */
  async rollback(deployId: number): Promise<RollbackDeployResponse> {
    const client = await this.getClient();
    const response = await client.POST("/api/v1/deploys/{id}/rollback", {
      params: {
        path: { id: deployId },
      },
    });

    if ((response as any).error) {
      throw new Error(`Failed to rollback deploy: ${JSON.stringify((response as any).error)}`);
    }

    if (!response.data) {
      throw new Error("Failed to rollback deploy: No data returned");
    }

    return response.data;
  }

  /**
   * Cancels an in-progress deploy.
   */
  async cancel(deployId: number): Promise<DeployRecord> {
    const client = await this.getClient();
    const response = await client.POST("/api/v1/deploys/{id}/cancel" as any, {
      params: {
        path: { id: deployId },
      },
    });

    if ((response as any).error) {
      throw new Error(`Failed to cancel deploy: ${JSON.stringify((response as any).error)}`);
    }

    if (!response.data) {
      throw new Error("Failed to cancel deploy: No data returned");
    }

    return response.data as DeployRecord;
  }

  /**
   * Updates the user_active_at timestamp for a deploy.
   * Called when the user is actively viewing the deploy page.
   */
  async touch(deployId: number): Promise<TouchDeployResponse> {
    const client = await this.getClient();
    const response = await client.POST("/api/v1/deploys/{id}/touch", {
      params: {
        path: { id: deployId },
      },
    });

    if (response.error) {
      throw new Error(`Failed to touch deploy: ${JSON.stringify(response.error)}`);
    }

    if (!response.data) {
      throw new Error("Failed to touch deploy: No data returned");
    }

    return response.data;
  }
}
